'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  ChevronRight,
  ShoppingCart,
  Droplet, Cookie, Wheat, Bean, Apple, Flame, Utensils, Hexagon, Star
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from './context/CartContext';
import { useAuth } from './context/AuthContext';
import { useLanguage } from './context/LanguageContext';
import { Navbar, Footer } from '../components/Layout';
import ShoppableVideoFeed from '../components/ShoppableVideoFeed';
import ProductImage from '../components/ProductImage';
import ProductCard from '../components/ProductCard';

const DUMMY_SWEETS = [
  { id: 101, name: 'Pure Palm Jaggery (Karupatti)', price: 340, oldPrice: 450, weight: '500g', badge: 'Best Seller', category: 'Sweeteners', tagText: 'Best Seller', tagColor: '#C8852A', foodPreference: 'VEG', averageRating: 5.0, reviewCount: 4 },
  { id: 102, name: 'Organic Jaggery Powder', price: 120, oldPrice: 150, weight: '500g', badge: 'Pure', category: 'Sweeteners', tagText: 'Pure', tagColor: '#1C3A2B', foodPreference: 'VEG', averageRating: 4.8, reviewCount: 3 },
  { id: 103, name: 'Raw Forest Wild Honey', price: 550, oldPrice: 650, weight: '250g', badge: 'Tribal Harvest', category: 'Sweeteners', tagText: 'Tribal Harvest', tagColor: '#C8852A', foodPreference: 'VEG', averageRating: 4.9, reviewCount: 12 },
  { id: 104, name: 'Dates Syrup', price: 220, oldPrice: null, weight: '250ml', badge: 'New', category: 'Sweeteners', tagText: 'New', tagColor: '#C8852A', foodPreference: 'VEG', averageRating: 4.5, reviewCount: 2 },
];

const DEFAULT_CATEGORIES = [
  { name: 'Oils & Ghee', icon: 'Droplet' },
  { name: 'Raw Honey', icon: 'Hexagon' },
  { name: 'Sweeteners', icon: 'Cookie' },
  { name: 'Millets', icon: 'Wheat' },
  { name: 'Spices', icon: 'Utensils' },
  { name: 'Snacks', icon: 'Apple' },
  { name: 'Dry Fruits', icon: 'Bean' }
];

const DEFAULT_SLIDES = [
  { image: "https://images.unsplash.com/photo-1542838132-92c53300491e?q=80&w=1974&auto=format&fit=crop", title: "Fresh Organic Harvest", sub: "Bringing the purest nature directly from our certified farms to your dining table.", ctaUrl: "/products" },
  { image: "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?q=80&w=2070&auto=format&fit=crop", title: "Traditional & Pure", sub: "100% natural, no heat extracted to preserve vital nutrients.", ctaUrl: "/products?category=Oils%20%26%20Ghee" },
  { image: "https://images.unsplash.com/photo-1590779033100-9f60a05a013d?q=80&w=1974&auto=format&fit=crop", title: "Sustainable Farming", sub: "Supporting local farmers and nurturing the soil for future generations.", ctaUrl: "/our-story" }
];

const SALEM_MEATS = [
  { id: 201, name: 'Country Chicken Curry Cut (Nattu Kozhi)', price: 320, category: 'Poultry', image: 'https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=500&auto=format&fit=crop&q=60', foodPreference: 'NON_VEG', slug: 'country-chicken-curry-cut' },
  { id: 202, name: 'Premium Salem Goat Mutton', price: 420, category: 'Red Meat', image: 'https://images.unsplash.com/photo-1603048588665-791ca8aea617?w=500&auto=format&fit=crop&q=60', foodPreference: 'NON_VEG', slug: 'premium-salem-goat-mutton' },
  { id: 203, name: 'Salem Farm Country Eggs (Nattu Kozhi Muttai)', price: 90, category: 'Eggs', image: 'https://images.unsplash.com/photo-1516448620398-c5f44bf9f441?w=500&auto=format&fit=crop&q=60', foodPreference: 'NON_VEG', slug: 'salem-farm-country-eggs' },
  { id: 204, name: 'Salem Special Kaadai (Quail)', price: 180, category: 'Poultry', image: 'https://images.unsplash.com/photo-1506976785307-8732e854ad03?w=500&auto=format&fit=crop&q=60', foodPreference: 'NON_VEG', slug: 'salem-special-kaadai' },
  { id: 205, name: 'Mettur Dam Dry Fish (Karuvadu)', price: 150, category: 'Sea Food', image: 'https://images.unsplash.com/photo-1534604973900-c43ab4c2e0ab?w=500&auto=format&fit=crop&q=60', foodPreference: 'NON_VEG', slug: 'mettur-dam-dry-fish' },
  { id: 206, name: 'Salem Mutton Thokku Pickle', price: 250, category: 'Pickles', image: 'https://images.unsplash.com/photo-1589301760014-d929f3979dbc?w=500&auto=format&fit=crop&q=60', foodPreference: 'NON_VEG', slug: 'salem-mutton-thokku-pickle' }
];

const getPremiumCategorySvg = (name: string) => {
  const lower = name.toLowerCase();
  if (lower.includes('oil')) {
    return (
      <svg viewBox="0 0 64 64" className="w-8 h-8 sm:w-11 sm:h-11 stroke-current stroke-[2.5] fill-none transition-transform duration-300 group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
        <path d="M24 16v-8h16v8M28 8h8M18 24c0-4.4 3.6-8 8-8h16c4.4 0 8 3.6 8 8v30c0 2.2-1.8 4-4 4H22c-2.2 0-4-1.8-4-4V24z" />
        <path d="M32 32v12M28 36l4-4 4 4" className="stroke-amber" />
        <circle cx="32" cy="24" r="2" className="fill-amber stroke-none" />
      </svg>
    );
  }
  if (lower.includes('ghee')) {
    return (
      <svg viewBox="0 0 64 64" className="w-8 h-8 sm:w-11 sm:h-11 stroke-current stroke-[2.5] fill-none transition-transform duration-300 group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 16h32M20 16v-6c0-2 2-4 4-4h16c2 0 4 2 4 4v6M12 28c0-6.6 5.4-12 12-12h16c6.6 0 12 5.4 12 12v22c0 4.4-3.6 8-8 8H20c-4.4 0-8-3.6-8-8V28z" />
        <path d="M26 36c2 6 10 6 12 0s2-10-6-10-10 4-6 10z" className="stroke-amber" />
        <path d="M32 16v8" className="stroke-amber" />
      </svg>
    );
  }
  if (lower.includes('honey')) {
    return (
      <svg viewBox="0 0 64 64" className="w-8 h-8 sm:w-11 sm:h-11 stroke-current stroke-[2.5] fill-none transition-transform duration-300 group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
        <path d="M32 4l18 10v21L32 46 14 35V14z" />
        <path d="M32 12l10 6v11l-10 6-10-6V18z" className="stroke-amber" />
        <path d="M32 20v6M28 23h8" className="stroke-amber" />
        <circle cx="50" cy="48" r="6" />
        <path d="M48 42l4 12" />
      </svg>
    );
  }
  if (lower.includes('sweet')) {
    return (
      <svg viewBox="0 0 64 64" className="w-8 h-8 sm:w-11 sm:h-11 stroke-current stroke-[2.5] fill-none transition-transform duration-300 group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 28l16-10 24 10-16 10zM12 28v16l16 10V38z" />
        <path d="M52 28v16L36 54V38z" />
        <path d="M22 14l12-8 18 8-12 8z" className="stroke-amber" />
      </svg>
    );
  }
  if (lower.includes('millet')) {
    return (
      <svg viewBox="0 0 64 64" className="w-8 h-8 sm:w-11 sm:h-11 stroke-current stroke-[2.5] fill-none transition-transform duration-300 group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 52c16 0 28-12 28-28V8c-12 0-24 12-24 28v16z" />
        <path d="M32 20c4 4 4 10 0 14s-10 4-14 0" className="stroke-amber" />
        <path d="M20 52l-8 4M44 24l8-4" />
      </svg>
    );
  }
  if (lower.includes('spice')) {
    return (
      <svg viewBox="0 0 64 64" className="w-8 h-8 sm:w-11 sm:h-11 stroke-current stroke-[2.5] fill-none transition-transform duration-300 group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 16h40v12c0 11-9 20-20 20S12 39 12 28V16zM22 48v8h20v-8" />
        <path d="M38 8l-8 14" className="stroke-amber" />
        <circle cx="30" cy="22" r="3" className="fill-amber stroke-none" />
      </svg>
    );
  }
  if (lower.includes('snack')) {
    return (
      <svg viewBox="0 0 64 64" className="w-8 h-8 sm:w-11 sm:h-11 stroke-current stroke-[2.5] fill-none transition-transform duration-300 group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="32" cy="42" rx="24" ry="14" />
        <ellipse cx="32" cy="40" rx="20" ry="10" />
        <circle cx="24" cy="36" r="4" className="stroke-amber" />
        <circle cx="32" cy="34" r="5" className="stroke-amber" />
        <circle cx="40" cy="36" r="4" className="stroke-amber" />
      </svg>
    );
  }
  if (lower.includes('non-veg') || lower.includes('meat')) {
    return (
      <svg viewBox="0 0 64 64" className="w-8 h-8 sm:w-11 sm:h-11 stroke-red-600 stroke-[2.5] fill-none transition-transform duration-300 group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 28c4-12 24-12 28 0s-8 28-28 0z" />
        <path d="M28 28c4 4 4 10 0 14s-10 4-14 0" className="stroke-red-600 fill-red-150" />
        <path d="M18 18s4 2 8 0 2-4 8 0" className="stroke-red-500" />
      </svg>
    );
  }
  // Default (Dry Fruits/Others)
  return (
    <svg viewBox="0 0 64 64" className="w-8 h-8 sm:w-11 sm:h-11 stroke-current stroke-[2.5] fill-none transition-transform duration-300 group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 28c8-16 20-16 28 0s-8 32-28 0z" />
      <path d="M24 28c4 4 4 10 0 14s-10 4-14 0" className="stroke-amber" />
    </svg>
  );
};

const getCategoryCount = (name: string) => {
  const lower = name.toLowerCase();
  if (lower.includes('oil')) return '8 Items';
  if (lower.includes('ghee')) return '4 Items';
  if (lower.includes('honey')) return '6 Items';
  if (lower.includes('sweetener')) return '10 Items';
  if (lower.includes('millet')) return '14 Items';
  if (lower.includes('spice')) return '18 Items';
  if (lower.includes('snack')) return '12 Items';
  if (lower.includes('meat') || lower.includes('non-veg')) return '6 Items';
  return '9 Items';
};

const CategoryCircles = ({ categories = DEFAULT_CATEGORIES, productsList = [] }: { categories?: any[], productsList?: any[] }) => {
  const { t } = useLanguage();
  const rawCats = categories && categories.length > 0 ? categories : DEFAULT_CATEGORIES;
  
  // Only use categories from admin panel, checking if category has active products
  const cats = rawCats.filter(c => {
    if (productsList.length === 0) return true;
    return productsList.some(p => {
      const pCat = p.category?.name || p.category || '';
      return pCat.toLowerCase() === c.name.toLowerCase();
    });
  });

  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-4 md:pt-10 md:pb-8 text-left">
      <div className="bg-transparent md:bg-gradient-to-br md:from-pale-green/30 md:via-[#FAF9F5] md:to-amber/5 md:border md:border-border/60 md:rounded-[2.5rem] p-0 md:p-12 md:shadow-inner">
        <h2 className="text-lg md:text-3xl font-display font-black text-deep-green mb-1 text-left md:text-center">{t('exploreRanges')}</h2>
        <p className="text-text-muted text-[10px] md:text-sm font-semibold mb-4 md:mb-8 uppercase tracking-widest text-left md:text-center">{t('localFarmsText')}</p>
        
        <div className="flex justify-start md:justify-center items-center overflow-x-auto pb-4 gap-4 sm:gap-6 md:gap-8 scrollbar-hide snap-x px-4 -mx-4 md:px-0 md:mx-0">
          {cats.map((cat, i) => {
            return (
              <Link 
                href={`/products?category=${encodeURIComponent(cat.name)}`}
                key={i} 
                className="flex flex-col items-center gap-2 cursor-pointer group min-w-[90px] sm:min-w-[110px] snap-center block text-center"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white border flex items-center justify-center transition-all shadow-sm group-hover:scale-105 duration-300 relative group-hover:border-mid-green border-border/80 text-deep-green group-hover:text-mid-green">
                  {/* Spinning gold dashed active/hover ring */}
                  <div className="absolute inset-0.5 rounded-full border border-dashed opacity-0 group-hover:opacity-100 group-hover:animate-[spin_20s_linear_infinite] transition-opacity duration-300 border-amber"></div>
                  {getPremiumCategorySvg(cat.name)}
                </div>
                <span className="text-[10px] md:text-xs font-black uppercase tracking-wider text-center mt-1 leading-none text-deep-green group-hover:text-mid-green">{cat.name}</span>
                <span className="text-[8px] text-text-muted font-bold uppercase tracking-widest leading-none mt-0.5">{getCategoryCount(cat.name)}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ProductSection = ({ title, products, addToCartOverride }: { title: string, products: any[], addToCartOverride?: (p: any) => void }) => {
  const { addToCart } = useCart();
  const { t } = useLanguage();
  const handleAdd = addToCartOverride || addToCart;
  
  if (products.length === 0) return null;

  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-6 md:py-12 text-left">
      <div className="flex justify-between items-center mb-6 md:mb-10 border-b border-border/40 pb-3 md:pb-4">
        <h2 className="text-lg md:text-3xl font-display font-black text-deep-green">{title}</h2>
        <Link href="/products" className="text-mid-green font-bold text-xs md:text-sm hover:underline flex items-center gap-0.5 md:gap-1">
          {t('viewAll')} <ChevronRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
        </Link>
      </div>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 md:gap-8">
        {products.map((p, i) => (
          <ProductCard 
            key={i} 
            product={p} 
            onAddToCart={handleAdd} 
          />
        ))}
      </div>
    </section>
  );
};

const BlogSection = () => {
  const [blogs, setBlogs] = useState<any[]>([]);
  const { t } = useLanguage();

  useEffect(() => {
    const productApi = process.env.NEXT_PUBLIC_PRODUCT_API || 'http://localhost:8081';
    fetch(`${productApi}/api/blogs`)
      .then(res => res.json())
      .then(data => {
        let list = [];
        if (data && data.data) {
          list = data.data;
        } else if (Array.isArray(data)) {
          list = data;
        }
        if (list.length > 0) setBlogs(list.slice(0, 4));
      })
      .catch(() => {});
  }, []);

  const dummyBlogs = [
    { id: 1, title: "The Incredible Benefits of Switching to A2 Ghee", createdAt: new Date("2023-10-12").toISOString(), type: "Health", image: "https://images.unsplash.com/photo-1620706857370-e1b9770e8bb1?q=80&w=600&auto=format&fit=crop", readTime: "5 mins read" },
    { id: 2, title: "Why Wood Pressed Oils are Better than Refined?", createdAt: new Date("2023-09-28").toISOString(), type: "Traditional", image: "https://images.unsplash.com/photo-1474625121024-7595bfbc57ac?q=80&w=600&auto=format&fit=crop", readTime: "4 mins read" },
    { id: 3, title: "Millet: The Ancient Superfood for Modern Life", createdAt: new Date("2023-09-15").toISOString(), type: "Nutrition", image: "https://images.unsplash.com/photo-1596647225114-11e5f8fbebd8?q=80&w=600&auto=format&fit=crop", readTime: "6 mins read" },
    { id: 4, title: "Organic Farming: Nurturing the Soil & Your Soul", createdAt: new Date("2023-08-30").toISOString(), type: "Farming", image: "https://images.unsplash.com/photo-1590779033100-9f60a05a013d?q=80&w=600&auto=format&fit=crop", readTime: "7 mins read" }
  ];

  const activeBlogs = blogs.map(b => {
    // Inject images/read times if missing in fetched articles
    const fb = dummyBlogs.find(d => d.id === b.id) || dummyBlogs[0];
    return {
      ...b,
      image: b.image || fb?.image || 'https://images.unsplash.com/photo-1620706857370-e1b9770e8bb1?q=80&w=600&auto=format&fit=crop',
      readTime: b.readTime || fb?.readTime || '5 mins read'
    };
  });

  const displayBlogs = activeBlogs;

  if (displayBlogs.length === 0) return null;

  return (
    <section className="bg-surface py-10 md:py-16 mt-6 md:mt-12 border-y border-border text-left">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="text-center mb-10 md:mb-12 max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-black text-deep-green mb-4">The Organic Lifestyle</h2>
          <p className="text-text-muted text-sm md:text-base leading-relaxed">Stories, recipes, and tips from our farmers and nutritionists to help you live a healthier life.</p>
        </div>
        
        {/* Sleeker 4x4 card layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayBlogs.map((b) => (
            <Link key={b.id} href={`/blog/${b.id}`} className="bg-white rounded-2xl overflow-hidden border border-border group hover:shadow-card transition-all duration-500 flex flex-col justify-between h-full cursor-pointer block text-left">
              <div className="h-36 bg-pale-green relative overflow-hidden shrink-0">
                {b.image ? (
                  <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500" />
                ) : (
                  <div className="w-full h-full bg-pale-green" />
                )}
                <div className="absolute top-3 left-3 bg-amber text-[#1A2418] px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest">{b.type}</div>
              </div>
              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-[10px] font-bold text-text-muted mb-2">
                    <span>{new Date(b.createdAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'})}</span>
                    <span className="text-mid-green">{b.readTime || '5 mins read'}</span>
                  </div>
                  <h3 className="text-sm font-bold text-deep-green mb-3 group-hover:text-mid-green transition-colors line-clamp-2 leading-snug">{b.title}</h3>
                </div>
                <div className="text-mid-green font-bold text-xs flex items-center gap-1.5 hover:translate-x-1.5 transition-transform block mt-2">
                  Read Story <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* View More Blogs Button */}
        <div className="flex justify-center mt-12">
          <Link href="/blogs" className="bg-mid-green hover:bg-deep-green text-white px-8 py-3 rounded-xl text-sm font-bold transition-all shadow-md uppercase tracking-wider">
            {t('viewMoreBlogs')}
          </Link>
        </div>
      </div>
    </section>
  );
};

const TrustRibbon = () => {
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-4 md:py-6">
      <div className="bg-white border border-border/85 rounded-2xl md:rounded-[2rem] p-4 md:p-8 shadow-sm flex flex-row overflow-x-auto md:grid md:grid-cols-5 gap-4 md:gap-6 md:divide-x divide-border/60 scrollbar-hide snap-x flex-nowrap">
        {[
          { icon: "🌿", title: "100% Certified Organic", desc: "NPOP certified organic farms" },
          { icon: "🚜", title: "Direct Farm Sourced", desc: "Fair trade supporting local farmers" },
          { icon: "🔬", title: "Lab-Tested Purity", desc: "Zero chemical or pesticide residues" },
          { icon: "📦", title: "Zero-Plastic Packing", desc: "Biodegradable earth-friendly packs" },
          { icon: "🚚", title: "Express Fresh Delivery", desc: "Harvest to home in under 24 hours" }
        ].map((item, index) => (
          <div key={index} className="flex flex-row md:flex-col items-center md:items-start text-left gap-3 p-1 md:px-4 shrink-0 snap-center min-w-[215px] md:min-w-0">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-pale-green/40 flex items-center justify-center text-lg shadow-inner border border-pale-green/20 shrink-0">
              {item.icon}
            </div>
            <div>
              <h4 className="text-[10px] md:text-xs font-black text-deep-green uppercase tracking-wider leading-tight">{item.title}</h4>
              <p className="text-[9px] md:text-[10px] text-text-muted font-bold mt-0.5 leading-tight">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const TestimonialsSection = ({ list }: { list?: any[] }) => {
  if (!list || list.length === 0) return null;
  const items = list;

  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-10 md:py-16 text-left">
      <div className="text-center mb-10 md:mb-12 max-w-2xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-display font-black text-deep-green mb-4 font-playfair">Loved by Our Community</h2>
        <div className="h-[2px] w-24 bg-amber rounded-full mx-auto mb-6"></div>
        <p className="text-text-muted text-sm md:text-base leading-relaxed">Hear from our patrons who support traditional organic farming and clean eating.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {items.map((t: any, i: number) => (
          <div key={i} className="bg-[#FAF9F5] border border-border/60 rounded-[2rem] p-6 md:p-8 relative overflow-hidden shadow-inner flex flex-col justify-between hover:border-amber/55 transition-all duration-300">
            <div className="absolute top-0 right-0 w-24 h-24 bg-amber/5 rounded-full blur-xl pointer-events-none"></div>
            <div>
              <div className="flex gap-1 mb-4">
                {[...Array(t.rating || 5)].map((_, idx) => (
                  <Star key={idx} className="w-4 h-4 fill-amber text-amber" />
                ))}
              </div>
              <p className="text-sm md:text-base text-deep-green font-medium italic leading-relaxed mb-6">
                "{t.quote}"
              </p>
            </div>
            <div className="border-t border-border/40 pt-4 flex items-center justify-between">
              <div>
                <span className="text-xs font-black text-deep-green uppercase tracking-wider block">{t.author}</span>
                <span className="text-[10px] text-text-muted font-bold tracking-wider uppercase block">{t.role || 'Patron'}</span>
              </div>
              <span className="text-[10px] text-amber font-black uppercase tracking-wider bg-amber/10 px-2 py-0.5 rounded border border-amber/20">{t.location}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Page() {
  const [products, setProducts] = useState<any[]>(() => {
    if (typeof window !== 'undefined') {
      const cached = sessionStorage.getItem('pv_products_cache');
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch (e) {}
      }
    }
    return [];
  });
  const [loading, setLoading] = useState(() => {
    if (typeof window !== 'undefined') {
      return !sessionStorage.getItem('pv_products_cache');
    }
    return true;
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [enableShoppableVideo, setEnableShoppableVideo] = useState(true);
  const { addToCart } = useCart();
  const { token, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const [userProfile, setUserProfile] = useState<any>(null);
  
  // Veg/Non-Veg homepage toggle state
  const [showNonVeg, setShowNonVeg] = useState(false);
  const [showNonVegMobile, setShowNonVegMobile] = useState(true);

  // Admin layouts state definitions
  const [carouselSlides, setCarouselSlides] = useState<any[]>(() => {
    if (typeof window !== 'undefined') {
      const cached = sessionStorage.getItem('pv_carousel_slides_cache');
      if (cached) {
        try { return JSON.parse(cached); } catch (e) {}
      }
    }
    return [];
  });
  const [marqueeMessage, setMarqueeMessage] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('pv_marquee_message_cache') || '';
    }
    return '';
  });
  const [testimonialsList, setTestimonialsList] = useState<any[]>(() => {
    if (typeof window !== 'undefined') {
      const cached = sessionStorage.getItem('pv_testimonials_cache');
      if (cached) {
        try { return JSON.parse(cached); } catch (e) {}
      }
    }
    return [];
  });
  const [categoryItems, setCategoryItems] = useState<any[]>(() => {
    if (typeof window !== 'undefined') {
      const cached = sessionStorage.getItem('pv_category_items_cache');
      if (cached) {
        try { return JSON.parse(cached); } catch (e) {}
      }
    }
    return DEFAULT_CATEGORIES;
  });
  const [sectionsList, setSectionsList] = useState<any[]>(() => {
    if (typeof window !== 'undefined') {
      const cached = sessionStorage.getItem('pv_sections_cache');
      if (cached) {
        try { return JSON.parse(cached); } catch (e) {}
      }
    }
    return [
      { title: 'Fresh From Farm', category: 'Oils & Ghee', limit: 4 },
      { title: 'Traditional Sweeteners', category: 'Sweeteners', limit: 4 }
    ];
  });
  
  const [flashDiscount, setFlashDiscount] = useState('Save 25%');
  const [flashStockLeft, setFlashStockLeft] = useState('15');
  const [flashHoursLeft, setFlashHoursLeft] = useState('2');
  const [flashProductId, setFlashProductId] = useState('');
  const [timeLeft, setTimeLeft] = useState(2 * 3600); // countdown state in seconds

  // Load user profile for Veg preference checking
  useEffect(() => {
    if (token && isAuthenticated) {
      const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
      fetch(`${authApi}/api/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => setUserProfile(data))
        .catch(err => console.warn('Could not fetch profile preference', err));
    } else {
      setUserProfile(null);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (userProfile) {
      setShowNonVeg(userProfile.foodPreference === 'NON_VEG');
      setShowNonVegMobile(userProfile.foodPreference !== 'VEG');
    } else {
      setShowNonVeg(false);
      setShowNonVegMobile(true);
    }
  }, [userProfile]);


  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_PRODUCT_API || 'http://localhost:8081';
    fetch(`${apiUrl}/api/products?size=100`)
      .then(res => res.json())
      .then(data => {
        let contentList: any[] = [];
        if (data && data.data && data.data.content) {
          contentList = data.data.content;
        } else if (Array.isArray(data)) {
          contentList = data;
        }

        if (contentList.length > 0) {
          const mappedProducts = contentList.map((p: any) => {
            const firstVariant = p.productVariants && p.productVariants.length > 0 ? p.productVariants[0] : null;
            return {
              id: p.id,
              slug: p.slug,
              name: p.name,
              price: firstVariant ? firstVariant.price : p.price,
              oldPrice: firstVariant ? firstVariant.oldPrice : null,
              weight: p.variants?.[0] || (firstVariant ? firstVariant.name : '1 Item'),
              badge: p.certifications?.[0] || 'Organic',
              category: p.categoryName || p.category?.name || p.category || 'Fresh Organic',
              image: p.images?.[0],
              images: p.images || [],
              tagText: p.tagText,
              tagColor: p.tagColor,
              foodPreference: p.foodPreference || 'VEG',
              averageRating: p.averageRating !== undefined ? p.averageRating : 5.0,
              reviewCount: p.reviewCount !== undefined ? p.reviewCount : 0,
              farmSource: p.farmSource,
              showFarmSource: p.showFarmSource,
              productVariants: p.productVariants
            };
          });
          setProducts(mappedProducts);
          if (typeof window !== 'undefined') {
            sessionStorage.setItem('pv_products_cache', JSON.stringify(mappedProducts));
          }
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Fetch error:', err);
        setLoading(false);
      });
  }, []);

  // Fetch settings parameters from backend
  useEffect(() => {
    const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
    fetch(`${authApi}/api/settings`)
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Failed to fetch settings');
      })
      .then(settings => {
        if (settings) {
          if (settings.enableShoppableVideo !== undefined) {
            setEnableShoppableVideo(settings.enableShoppableVideo === 'true');
          }

          if (settings.flashDiscount) setFlashDiscount(settings.flashDiscount);
          if (settings.flashStockLeft) setFlashStockLeft(settings.flashStockLeft);
          if (settings.flashSaleEnd) {
            const diffMs = new Date(settings.flashSaleEnd).getTime() - Date.now();
            const diffSec = Math.round(diffMs / 1000);
            setTimeLeft(diffSec > 0 ? diffSec : 0);
          } else if (settings.flashHoursLeft) {
            setFlashHoursLeft(settings.flashHoursLeft);
            const hrs = parseFloat(settings.flashHoursLeft);
            if (!isNaN(hrs)) {
              setTimeLeft(Math.round(hrs * 3600));
            }
          }
          if (settings.flashProductId) setFlashProductId(settings.flashProductId);

          if (settings.heroSlides) {
            try {
              setCarouselSlides(JSON.parse(settings.heroSlides));
            } catch (e) {
              console.warn('Error parsing heroSlides', e);
              setCarouselSlides(DEFAULT_SLIDES);
            }
          } else {
            setCarouselSlides(DEFAULT_SLIDES);
          }
          if (settings.marqueeMessage) {
            setMarqueeMessage(settings.marqueeMessage);
          }
          if (settings.homepage_testimonials) {
            try {
              setTestimonialsList(JSON.parse(settings.homepage_testimonials));
            } catch (e) { console.warn('Error parsing homepage_testimonials', e); }
          }
          if (settings.categoryItems) {
            try {
              setCategoryItems(JSON.parse(settings.categoryItems));
            } catch (e) { console.warn('Error parsing categoryItems', e); }
          }
          if (settings.homepage_sections) {
            try {
              setSectionsList(JSON.parse(settings.homepage_sections));
            } catch (e) { console.warn('Error parsing homepage_sections', e); }
          }
        } else {
          setCarouselSlides(DEFAULT_SLIDES);
        }
      })
      .catch(err => {
        console.error('Failed to load settings:', err);
        setCarouselSlides(DEFAULT_SLIDES);
      });
  }, []);

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return {
      hours: String(hrs).padStart(2, '0'),
      minutes: String(mins).padStart(2, '0'),
      seconds: String(secs).padStart(2, '0')
    };
  };

  const timerFormatted = formatTime(timeLeft);

  const handleAddToCart = (product: any) => {
    addToCart(product);
    setToastMsg(`${product.name} added to cart!`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const defaultFlashProduct = {
    id: 101,
    name: 'Organic Apples (Fresh Farm Pick)',
    price: 340,
    oldPrice: 450,
    category: 'Fresh Fruits',
    image: 'https://images.unsplash.com/photo-1560806887-1e4cd0b6fac6?w=500&auto=format&fit=crop&q=60',
    foodPreference: 'VEG',
    averageRating: 4.8,
    reviewCount: 16
  };

  // Determine active flash sale product details from DB or fallbacks
  let flashProduct: any = defaultFlashProduct;
  if (products.length > 0) {
    if (flashProductId) {
      const match = products.find(p => String(p.id) === String(flashProductId));
      if (match) {
        flashProduct = match;
      } else {
        flashProduct = products[0];
      }
    } else {
      flashProduct = products[0];
    }
  }

  // Filter section products dynamically with strict filtering and Veg filtering
  const getSectionProducts = (categoryName: string, defaultProducts: any[], maxCount: number = 4) => {
    let sourceList = products;
    if (userProfile?.foodPreference === 'VEG') {
      sourceList = products.filter(p => p.foodPreference !== 'NON_VEG');
    }
    
    if (sourceList.length === 0) {
      return [];
    }

    const filtered = sourceList.filter(p => {
      const pCat = (p.categoryName || p.category || '').toLowerCase().trim();
      const sCat = categoryName.toLowerCase().trim();
      return pCat === sCat || pCat.includes(sCat) || sCat.includes(pCat);
    });
    return filtered.slice(0, maxCount);
  };

  // Hide flash sale if no products exist or if product is non-veg and user preference is VEG
  const shouldShowFlashSale = products.length > 0 && !(userProfile?.foodPreference === 'VEG' && flashProduct?.foodPreference === 'NON_VEG');

  // Filter Salem meats
  const nonVegProducts = products.filter(p => p.foodPreference === 'NON_VEG').slice(0, 6);

  return (
    <div className="min-h-screen bg-[#F8F9F5] font-body text-text-primary relative selection:bg-amber/30 selection:text-deep-green">
      <Navbar />

      {/* Toast Notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-10 right-10 z-[100] bg-deep-green text-white px-6 py-4 rounded-xl shadow-2xl hidden md:flex items-center gap-3 border border-amber/30 text-left"
          >
            <div className="w-8 h-8 bg-amber rounded-full flex items-center justify-center text-deep-green font-bold">✓</div>
            <span className="font-bold">{toastMsg}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <main>
        <div className="bg-gradient-to-b from-surface/50 to-transparent pb-2">
          <section className="max-w-[1400px] mx-auto px-4 md:px-12 pt-4 md:pt-6 pb-2 grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
            
            {/* Carousel Slider */}
            <HeroSectionContent slides={carouselSlides} />

            {/* Flash Sale Widget (Sleeker and same shape/size as product card on mobile view) */}
            {shouldShowFlashSale ? (
              <div className="bg-[#FCFBF7] text-[#1A2418] rounded-2xl p-4 flex flex-col justify-between relative overflow-hidden shadow-card border border-amber/30 max-w-md mx-auto w-full h-[450px] hidden lg:flex group/flash">
                <div className="absolute top-0 right-0 bg-amber text-white px-2.5 py-1 rounded-bl-xl font-black text-[9px] uppercase tracking-widest shadow-sm z-10 animate-pulse">Flash Sale</div>

                <Link href={`/product/${flashProduct.slug || flashProduct.id}`} className="block cursor-pointer flex-1 flex flex-col justify-between">
                  <div className="relative w-full h-[200px] lg:h-[210px] bg-surface rounded-xl overflow-hidden border border-border/50 group-hover/flash:border-mid-green shadow-inner shrink-0 mb-2">
                    <ProductImage src={flashProduct.image || 'https://images.unsplash.com/photo-1560806887-1e4cd0b6fac6?w=500&auto=format&fit=crop&q=60'} alt="Flash Sale Product" className="absolute inset-0 w-full h-full object-cover group-hover/flash:scale-105 transition-transform duration-500" />
                    {parseInt(flashStockLeft) <= 0 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-sm tracking-wider uppercase z-10">Sold Out</div>
                    )}
                  </div>

                  <div className="w-full text-left flex flex-col flex-1 justify-between">
                    <div>
                      <span className="bg-amber/10 text-amber text-[9px] font-black px-2 py-0.5 rounded mb-1.5 uppercase tracking-widest border border-amber/20 w-fit inline-block">{flashDiscount}</span>
                      <h3 className="text-xs sm:text-sm lg:text-base font-bold text-deep-green mb-1 leading-tight line-clamp-1 group-hover/flash:text-mid-green transition-colors">{flashProduct.name}</h3>
                    </div>

                    <div className="flex gap-2 items-end mb-2">
                      <span className="text-sm sm:text-base lg:text-xl font-black text-amber leading-none">₹{flashProduct.price}</span>
                      {flashProduct.oldPrice && <span className="text-text-muted line-through text-[10px] mb-0.5 font-bold">₹{flashProduct.oldPrice}</span>}
                    </div>
                  </div>
                </Link>

                <div className="w-full text-left mt-2">
                  <button
                    disabled={parseInt(flashStockLeft) <= 0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddToCart(flashProduct);
                    }}
                    className={`w-full py-1.5 lg:py-2 rounded-lg lg:rounded-xl font-bold uppercase tracking-wide transition-all mb-2 text-[10px] lg:text-xs shadow-md ${
                      parseInt(flashStockLeft) <= 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                        : 'bg-mid-green text-white hover:bg-deep-green active:scale-98'
                    }`}
                  >
                    {parseInt(flashStockLeft) <= 0 ? 'Out of Stock' : t('addToCart')}
                  </button>

                  <div className="w-full bg-gray-100 rounded-full h-1 mb-1 overflow-hidden">
                    <div className="bg-amber h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, (parseInt(flashStockLeft) / 20) * 100))}%` }}></div>
                  </div>
                  <div className="flex justify-between items-center text-[8px] lg:text-[9px] font-bold uppercase tracking-wider text-text-muted mb-2">
                    <span>{parseInt(flashStockLeft) <= 0 ? 'Sold Out!' : `Only ${flashStockLeft} left!`}</span>
                    <div className="flex gap-1 text-deep-green font-mono text-[9px] font-bold">
                      <span>{timerFormatted.hours}</span><span>:</span>
                      <span>{timerFormatted.minutes}</span><span>:</span>
                      <span className="text-amber">{timerFormatted.seconds}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-b from-deep-green to-[#0a2818] text-white rounded-2xl p-6 flex flex-col items-center justify-center text-center relative overflow-hidden shadow-xl border border-white/10 h-[380px] lg:h-[450px] w-full hidden lg:flex">
                <span className="text-amber font-bold text-xs uppercase tracking-widest block mb-4">Pasumai Virumbi</span>
                <h3 className="font-display font-black text-xl mb-4 leading-tight">Fresh harvest is waiting!</h3>
                <p className="text-white/70 text-xs leading-relaxed">Browse our catalog to get organic goodies pick-locked for you.</p>
                <Link href="/products" className="mt-6 bg-amber text-deep-green px-5 py-2.5 rounded-lg font-bold uppercase tracking-wider text-[10px] lg:text-xs hover:bg-white transition-colors">Browse Products</Link>
              </div>
            )}
          </section>

          {/* Scrolling Marquee Message Ticker */}
          {marqueeMessage && (
            <div className="bg-amber text-[#1A2418] py-2.5 overflow-hidden select-none border-y border-amber-600/20 relative z-10 w-full my-4 shadow-sm">
              <div className="animate-marquee flex gap-8 whitespace-nowrap">
                <div className="flex gap-8 shrink-0">
                  {[...Array(4)].map((_, i) => (
                    <React.Fragment key={i}>
                      <span className="text-xs sm:text-sm font-black uppercase tracking-widest">{marqueeMessage}</span>
                      <span className="text-amber-800 font-bold">•</span>
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex gap-8 shrink-0">
                  {[...Array(4)].map((_, i) => (
                    <React.Fragment key={i}>
                      <span className="text-xs sm:text-sm font-black uppercase tracking-widest">{marqueeMessage}</span>
                      <span className="text-amber-800 font-bold">•</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Premium 5-Item Trust Ribbon directly below hero */}
            {/* Harvest Deals / Flash Sale Section on Mobile */}
          {shouldShowFlashSale && (
            <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 lg:hidden block text-left animate-fade-in">
              <div className="bg-gradient-to-br from-amber/20 via-[#FCFBF7] to-pale-green/20 border border-amber/30 rounded-2xl p-3 shadow-sm relative overflow-hidden flex flex-row gap-3 items-center">
                <div className="absolute top-0 right-0 bg-amber text-deep-green px-2 py-0.5 rounded-bl-xl font-black text-[8px] uppercase tracking-wider shadow-sm z-10 animate-pulse">Harvest Deal</div>
                
                {/* Left side: Image */}
                <Link href={`/product/${flashProduct.slug || flashProduct.id}`} className="relative w-24 h-24 sm:w-28 sm:h-28 bg-surface rounded-xl overflow-hidden border border-border/50 shrink-0 shadow-inner block">
                  <ProductImage src={flashProduct.image} alt={flashProduct.name} className="absolute inset-0 w-full h-full object-cover" />
                  {parseInt(flashStockLeft) <= 0 && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-[10px] tracking-wider uppercase z-10">Sold Out</div>
                  )}
                </Link>

                {/* Right side: details, price, timer, and action button */}
                <div className="flex-grow flex flex-col justify-between h-24 sm:h-28 min-w-0">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="bg-amber/10 text-amber text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest border border-amber/20 w-fit inline-block">{flashDiscount}</span>
                      {parseInt(flashStockLeft) > 0 && (
                        <span className="text-[8px] text-red-500 font-bold uppercase tracking-wider">Only {flashStockLeft} left!</span>
                      )}
                    </div>
                    <Link href={`/product/${flashProduct.slug || flashProduct.id}`} className="text-xs sm:text-sm font-bold text-deep-green leading-tight line-clamp-1 block hover:text-mid-green">
                      {flashProduct.name}
                    </Link>
                  </div>

                  <div className="flex items-center justify-between gap-2 my-1">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-sm sm:text-base font-black text-amber leading-none">₹{flashProduct.price}</span>
                      {flashProduct.oldPrice && <span className="text-text-muted line-through text-[9px] font-semibold">₹{flashProduct.oldPrice}</span>}
                    </div>
                    
                    <div className="flex gap-0.5 text-deep-green font-mono text-[9px] font-bold bg-amber/5 px-1.5 py-0.5 rounded border border-amber/10">
                      <span>{timerFormatted.hours}</span><span>:</span>
                      <span>{timerFormatted.minutes}</span><span>:</span>
                      <span className="text-amber">{timerFormatted.seconds}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 bg-gray-100 rounded-full h-1 overflow-hidden">
                      <div className="bg-amber h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, (parseInt(flashStockLeft) / 20) * 100))}%` }}></div>
                    </div>
                    <button
                      disabled={parseInt(flashStockLeft) <= 0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAddToCart(flashProduct);
                      }}
                      className={`px-3 py-1 rounded-md font-bold uppercase tracking-wide transition-all text-[9px] shadow-sm shrink-0 ${
                        parseInt(flashStockLeft) <= 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                          : 'bg-mid-green text-white hover:bg-deep-green'
                      }`}
                    >
                      {parseInt(flashStockLeft) <= 0 ? 'Out' : 'Add'}
                    </button>
              </div>
            </div>
          </div>
        </section>
      )}
                  {/* Redesigned Isolated Categories Circle list - commented out as requested */}
          {/* <CategoryCircles categories={categoryItems} productsList={products} /> */}
 
          {/* Premium banner linking to /non-veg */}
          {isAuthenticated && userProfile?.foodPreference === 'NON_VEG' && (
            <section className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-4 md:py-6 text-left">
              <Link href="/non-veg" className="block relative rounded-3xl overflow-hidden bg-red-50/60 hover:bg-red-50 p-6 sm:p-10 border border-red-200 shadow-sm transition-all duration-300 group">
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <div>
                    <span className="bg-red-100 text-red-700 font-black text-[9px] px-2.5 py-0.5 rounded-full uppercase tracking-wider mb-2 inline-block border border-red-200">Salem Special</span>
                    <h3 className="text-xl sm:text-3xl font-display font-black text-red-900 mb-2 group-hover:text-red-700 transition-colors">🥩 Explore Premium Meats & Fish</h3>
                    <p className="text-text-muted text-xs sm:text-sm">Get antibiotic-free country chicken, Salem mutton, fresh Metter Dam fish, and farm eggs.</p>
                  </div>
                  <div className="bg-red-700 group-hover:bg-red-800 text-white px-6 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs shadow-md shrink-0 flex items-center gap-2">
                    Order Now <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </section>
          )}
        </div>
        
        {loading ? (
          <div className="text-center py-20 text-deep-green text-xl font-bold animate-pulse">Harvesting fresh products...</div>
        ) : (
          <>
            {/* Dynamic settings-controlled product sections */}
            {sectionsList.map((sec, idx) => {
              const secProducts = getSectionProducts(sec.category, [], sec.limit || 4);
              return (
                <ProductSection 
                  key={idx}
                  title={sec.title} 
                  products={secProducts} 
                  addToCartOverride={handleAddToCart} 
                />
              );
            })}
          </>
        )}

        {enableShoppableVideo && <ShoppableVideoFeed />}
        
        <TestimonialsSection list={testimonialsList} />
        
        <BlogSection />
      </main>
      <Footer />
    </div>
  );
}

const HeroSectionContent = ({ slides }: { slides?: any[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const activeSlides = slides || [];

  useEffect(() => {
    if (activeSlides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((p) => (p + 1) % activeSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [activeSlides]);

  // If slides are not loaded yet, show the premium skeleton loader block
  if (activeSlides.length === 0) {
    return (
      <div className="lg:col-span-3 relative h-[240px] sm:h-[250px] md:h-[350px] lg:h-[450px] rounded-2xl overflow-hidden bg-gradient-to-r from-pale-green/20 via-white/50 to-amber/10 animate-pulse flex flex-col justify-center px-6 sm:px-12 md:px-20 border border-border/50">
        <div className="w-24 h-6 bg-gray-200 rounded-full mb-4"></div>
        <div className="w-2/3 h-12 bg-gray-200 rounded-lg mb-4"></div>
        <div className="w-1/2 h-6 bg-gray-200 rounded-lg mb-6"></div>
        <div className="w-32 h-10 bg-gray-200 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="lg:col-span-3 relative h-[240px] sm:h-[250px] md:h-[350px] lg:h-[450px] rounded-2xl overflow-hidden bg-deep-green group shadow-card select-none">

      <AnimatePresence initial={false} mode="popLayout">
        {activeSlides.map((slide, idx) => {
          const isCurrent = idx === currentSlide;
          // Overlay Text & Button toggles
          const showText = slide.showText !== false && slide.showText !== 'false';
          const showButton = slide.showButton !== false && slide.showButton !== 'false';

          return isCurrent && (
            <motion.div
              key={idx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={(event, info) => {
                const swipeThreshold = 50;
                if (info.offset.x < -swipeThreshold) {
                  setCurrentSlide((p) => (p + 1) % activeSlides.length);
                } else if (info.offset.x > swipeThreshold) {
                  setCurrentSlide((p) => (p - 1 + activeSlides.length) % activeSlides.length);
                }
              }}
              className="absolute inset-0 flex flex-col justify-center text-white bg-cover bg-center cursor-grab active:cursor-grabbing"
              style={{ 
                backgroundImage: `url(${slide.image})`
              }}
            >
              {/* Entire slide is wrapped in a link block for background clicks */}
              <Link href={slide.ctaUrl || '/products'} className="absolute inset-0 z-0 cursor-pointer block">
                {showText && (
                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
                )}
              </Link>

              {showText && (
                <div className="relative z-10 max-w-xl text-left px-6 sm:px-12 md:px-20 pointer-events-none">
                  <span className="bg-amber text-deep-green px-3 py-0.5 sm:px-4 sm:py-1 text-[9px] sm:text-xs font-bold rounded-full mb-2 sm:mb-4 inline-block uppercase tracking-wider">
                    100% Organic
                  </span>
                  <h2 className="text-xl sm:text-3xl md:text-5xl font-display font-black mb-2 sm:mb-4 leading-tight">
                    {slide.title}
                  </h2>
                  <p className="text-xs sm:text-base md:text-lg mb-3 sm:mb-6 text-white/95 leading-relaxed font-medium line-clamp-2 md:line-clamp-none font-sans">
                    {slide.sub}
                  </p>
                  
                  {showButton && (
                    <div className="bg-[#1C3A2B] text-white hover:bg-amber hover:text-deep-green border border-amber/20 px-6 py-2.5 sm:px-8 sm:py-4 rounded-xl font-black shadow-lg inline-block uppercase tracking-widest text-[9px] sm:text-xs pointer-events-auto transition-all cursor-pointer hover:scale-102 active:scale-98">
                      Shop the Harvest
                    </div>
                  )}

                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3.5 text-[9px] sm:text-xs font-bold text-white/90">
                    <span className="flex items-center gap-1">✓ Certified Organic</span>
                    <span className="flex items-center gap-1">✓ Farm Fresh Delivery</span>
                    <span className="flex items-center gap-1">✓ Chemical-Free Farming</span>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-20">
        {activeSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentSlide(i)}
            className={`h-2.5 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-amber w-8 font-black' : 'bg-white/50 w-2.5 hover:bg-white'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
